"use client";

import { AlertTriangle } from "lucide-react";

import { usePageTranslation } from "@/hooks/usePageTranslation";

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: "danger" | "warning" | "info";
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  variant = "warning",
}: ConfirmDialogProps) {
  const { t } = usePageTranslation();

  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      button:
        "bg-brand-danger text-brand-danger-foreground hover:bg-brand-danger/90",
      icon: "text-brand-danger",
      bg: "bg-brand-danger/10",
    },
    warning: {
      button:
        "bg-brand-primary text-brand-primary-foreground hover:bg-brand-primary-hover",
      icon: "text-brand-primary",
      bg: "bg-brand-primary/10",
    },
    info: {
      button: "bg-blue-600 text-white hover:bg-blue-700",
      icon: "text-blue-600",
      bg: "bg-blue-50",
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4 backdrop-blur-sm">
      <div className="animate-in fade-in zoom-in w-full max-w-md rounded-brand-lg bg-brand-surface shadow-2xl duration-200">
        {/* Icon and Title */}
        <div className="px-6 pt-6 pb-4">
          <div
            className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${styles.bg}`}
          >
            <AlertTriangle className={`h-6 w-6 ${styles.icon}`} />
          </div>
          <h3 className="mb-2 text-center text-lg font-semibold text-brand-fg">
            {title}
          </h3>
          <p className="text-center text-sm text-brand-muted">{message}</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onCancel}
            className="flex-1 rounded-brand border border-brand-border px-4 py-2 font-medium text-brand-fg transition-colors hover:bg-brand-fg/5"
          >
            {cancelText || t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 rounded-brand px-4 py-2 font-medium transition-colors ${styles.button}`}
          >
            {confirmText || t("account.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
